import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  Clip,
  type Op,
  Track,
  alsWav,
  aussteuerung,
  aussteuerungSatz,
  balkenAnteil,
  bandZeit,
  behalte,
  ersetzeAb,
  formatTimecode,
  loesche,
  mikrofonWunsch,
  spitzeText,
  spitzenVon,
  timelineDuration,
  zusammen,
} from '@schnitt/shared';
import { api } from '../api';
import { ausDatei, useEditor } from '../store';
import { nextFreeStart } from '../edits';
import { newId } from '../ids';
import { useZeitText } from '../zeit';
import { Icon } from './Icon';
import { WorkerWarning, useWorkerAlive } from './WorkerWarning';
import { Sheet } from './Sheet';

/**
 * **Der Rekorder für das Voiceover -- wie der Sprachrekorder von Apple**
 * (Sascha, 15. Oktober: „VoiceOver verarbeitet leider nicht. Habe eines
 * aufnehmen wollen, ging aber nicht. Können wir das ein bisschen mehr machen
 * wie den Apple Sprachrekorder? Ich kann dann anhören, zurückspulen,
 * ersetzen, kürzen … also Vollbildfenster mit guter
 * Bearbeitungsmöglichkeit“).
 *
 * Über den ganzen Schirm. Aufgenommen wird ab dem Abspielkopf, der Schnitt
 * läuft stumm mit. Danach steht die Aufnahme als Wellenform da: anhören,
 * 15 Sekunden zurück und vor, an jede Stelle tippen, ab dort **ersetzen**
 * (neu darübersprechen), **kürzen** (nur einen Teil behalten oder einen Teil
 * löschen). Erst „In den Film“ macht daraus eine Datei, lädt sie hoch und
 * legt sie auf eine Tonspur, an die Stelle, an der die Aufnahme begann.
 *
 * Aufgenommen wird nicht mehr über den `MediaRecorder` -- jeder Browser kann
 * dort etwas anderes, und eine fertige Datei lässt sich nicht an einer Stelle
 * überschreiben. Ein `AudioWorklet` (`/band-aufnahme.js`) reicht die rohen
 * Werte herauf; gerechnet wird in `tonband.ts`, hochgeladen als WAV.
 *
 * iOS: das Mikrofon und der Ton werden aus der Berührung heraus geholt
 * (Safari fragt sonst nicht), und die Wiedergabe läuft über einen eigenen
 * `AudioContext`, wie die Tonkette.
 */

type Phase = 'leer' | 'aufnahme' | 'band' | 'hochladen' | 'aufbereiten' | 'fertig';

/** Der Schalter für den lauten Raum bleibt, wo er zuletzt stand. */
const SCHALTER = 'schnitt.mikrofon-aufbereiten';
function leseSchalter(): boolean {
  try {
    return localStorage.getItem(SCHALTER) === 'ja';
  } catch {
    return false;
  }
}
function schreibeSchalter(an: boolean): void {
  try {
    localStorage.setItem(SCHALTER, an ? 'ja' : 'nein');
  } catch {
    /* Privater Modus: dann gilt er eben nur für diese Sitzung. */
  }
}

/** Wie weit „zurück“ und „vor“ springen -- wie im Sprachrekorder. */
const SPRUNG = 15;

/** Was gerade aufnimmt: der Kontext, die Werte, und wohin sie gehören. */
interface Aufnahme {
  ctx: AudioContext;
  stuecke: Float32Array[];
  /** Ab welcher Stelle des Bandes (Abtastwerte) überschrieben wird; `null` heisst: ein neues Band. */
  ersetzeAb: number | null;
  stopp: () => void;
}

export function VoiceoverSheet({ onClose }: { onClose: () => void }): ReactElement {
  const zeit = useZeitText();
  const project = useEditor((s) => s.project);
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const setPlaying = useEditor((s) => s.setPlaying);
  const setMonitorMuted = useEditor((s) => s.setMonitorMuted);
  const refreshAssets = useEditor((s) => s.refreshAssets);
  const dispatch = useEditor((s) => s.dispatch);
  const select = useEditor((s) => s.select);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);

  const [phase, setPhase] = useState<Phase>('leer');
  /** Das Band: die Aufnahme als Werte, und ihre Rate. */
  const [band, setBand] = useState<{ werte: Float32Array; rate: number } | null>(null);
  /** Wo im Film das Band beginnt -- festgehalten beim ersten Wort. */
  const [ab, setAb] = useState(0);
  /** Wo auf dem Band der Kopf steht, in Sekunden. */
  const [pos, setPos] = useState(0);
  const [spielt, setSpielt] = useState(false);
  /** Kürzen: der Bereich zwischen den beiden Griffen, in Sekunden. */
  const [kuerzen, setKuerzen] = useState<{ von: number; bis: number } | null>(null);
  const [laufzeit, setLaufzeit] = useState(0);
  const [level, setLevel] = useState(0);
  /** Die lauteste Spitze seit dem Anlegen des Mikrofons -- sie bleibt stehen. */
  const [hoechste, setHoechste] = useState(0);
  const [aufbereiten, setAufbereiten] = useState(leseSchalter);
  /** Läuft die Anzeige, ohne dass aufgenommen wird? („Pegel prüfen“) */
  const [probe, setProbe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [liveSpitzen, setLiveSpitzen] = useState<number[]>([]);

  const stream = useRef<MediaStream | null>(null);
  const aufnahme = useRef<Aufnahme | null>(null);
  const meter = useRef<{ ctx: AudioContext; raf: number; eigen: boolean } | null>(null);
  const wiedergabe = useRef<{ ctx: AudioContext; quelle: AudioBufferSourceNode | null; start: number; ab: number; raf: number } | null>(null);
  const gestartet = useRef(0);

  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof AudioContext !== 'undefined';
  const fps = timeline.settings.fps;
  const dauer = band ? band.werte.length / band.rate : 0;

  const stopMeter = () => {
    if (!meter.current) return;
    cancelAnimationFrame(meter.current.raf);
    if (meter.current.eigen) void meter.current.ctx.close();
    meter.current = null;
  };

  const releaseMic = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  };

  const halteWiedergabe = () => {
    const w = wiedergabe.current;
    if (!w?.quelle) return;
    cancelAnimationFrame(w.raf);
    w.quelle.onended = null;
    try {
      w.quelle.stop();
    } catch {
      /* schon zu Ende */
    }
    w.quelle = null;
    setSpielt(false);
  };

  // Was auch passiert: wer das Blatt verlässt, bekommt Ton und Mikrofon zurück.
  useEffect(
    () => () => {
      aufnahme.current?.stopp();
      aufnahme.current = null;
      halteWiedergabe();
      void wiedergabe.current?.ctx.close();
      stopMeter();
      releaseMic();
      setMonitorMuted(false);
      setPlaying(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMonitorMuted, setPlaying],
  );

  /**
   * Das Mikrofon holen -- so, wie es klingen soll. Muss aus einer Berührung
   * heraus aufgerufen werden: Safari fragt sonst gar nicht erst.
   */
  const holeMikrofon = async (): Promise<MediaStream | null> => {
    setError(null);
    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: mikrofonWunsch(aufbereiten) });
      stream.current = mic;
      return mic;
    } catch (err) {
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Mikrofon gesperrt. Im Browser für diese Seite freigeben.'
          : `Kein Mikrofon gefunden${err instanceof Error && err.message ? ` (${err.message})` : ''}.`,
      );
      return null;
    }
  };

  /** Die Anzeige: ein paar Dutzend Messungen die Sekunde reichen für einen Balken. */
  const starteMesser = (mic: MediaStream, ctxVorhanden?: AudioContext) => {
    setLevel(0);
    setHoechste(0);
    try {
      const ctx = ctxVorhanden ?? new AudioContext();
      const source = ctx.createMediaStreamSource(mic);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (const v of data) peak = Math.max(peak, Math.abs(v - 128) / 128);
        setLevel(peak);
        setHoechste((alt) => (peak > alt ? peak : alt));
        if (meter.current) meter.current.raf = requestAnimationFrame(tick);
      };
      meter.current = { ctx, raf: requestAnimationFrame(tick), eigen: !ctxVorhanden };
    } catch {
      // Keine Anzeige ist kein Grund, nicht aufzunehmen.
    }
  };

  /** Erst hören, dann aufnehmen: der Abstand zum Mikrofon stimmt dann beim ersten Take. */
  const pruefePegel = async () => {
    if (probe) {
      stopMeter();
      releaseMic();
      setProbe(false);
      return;
    }
    const mic = await holeMikrofon();
    if (!mic) return;
    starteMesser(mic);
    setProbe(true);
  };

  /**
   * Aufnehmen -- ein neues Band, oder ab `ersetzeBei` (Sekunden) darüber.
   * Der Schnitt läuft stumm mit, ab der Stelle im Film, die zu dieser Stelle
   * des Bandes gehört.
   */
  const nimmAuf = async (ersetzeBei: number | null) => {
    halteWiedergabe();
    if (probe) {
      stopMeter();
      releaseMic();
      setProbe(false);
    }
    setKuerzen(null);
    const mic = await holeMikrofon();
    if (!mic) return;
    let ctx: AudioContext;
    try {
      ctx = new AudioContext();
      if (ctx.state === 'suspended') await ctx.resume();
    } catch (err) {
      releaseMic();
      setError(`Der Ton dieses Browsers lässt sich nicht öffnen${err instanceof Error ? ` (${err.message})` : ''}.`);
      return;
    }
    const quelle = ctx.createMediaStreamSource(mic);
    // Was der Baustein ausgibt, ist Stille; angeschlossen sein muss er
    // trotzdem, sonst ruft ihn mancher Browser gar nicht erst.
    const still = ctx.createGain();
    still.gain.value = 0;
    still.connect(ctx.destination);
    const stuecke: Float32Array[] = [];
    let spitzenSeit = 0;
    const nimm = (werte: Float32Array) => {
      stuecke.push(werte);
      // Die Wellenform beim Aufnehmen: zwanzig Balken die Sekunde.
      spitzenSeit += werte.length;
      if (spitzenSeit >= ctx.sampleRate / 20) {
        spitzenSeit = 0;
        const letzte = stuecke.slice(-Math.ceil(ctx.sampleRate / 20 / Math.max(1, werte.length)));
        setLiveSpitzen((alt) => [...alt.slice(-400), spitzenVon(zusammen(letzte), 1)[0] ?? 0]);
      }
    };
    let trennen: () => void;
    try {
      if (!ctx.audioWorklet) throw new Error('kein AudioWorklet');
      await ctx.audioWorklet.addModule('/band-aufnahme.js');
      const knoten = new AudioWorkletNode(ctx, 'band-aufnahme', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1, channelCountMode: 'explicit' });
      knoten.port.onmessage = (e: MessageEvent<Float32Array>) => nimm(e.data);
      quelle.connect(knoten);
      knoten.connect(still);
      trennen = () => {
        knoten.port.onmessage = null;
        quelle.disconnect();
        knoten.disconnect();
      };
    } catch {
      // Ältere Browser: der alte Weg über einen ScriptProcessor. Er läuft im
      // Hauptfaden und kann bei viel Arbeit ruckeln -- aber er nimmt auf.
      const knoten = ctx.createScriptProcessor(4096, 1, 1);
      knoten.onaudioprocess = (e) => nimm(new Float32Array(e.inputBuffer.getChannelData(0)));
      quelle.connect(knoten);
      knoten.connect(still);
      trennen = () => {
        knoten.onaudioprocess = null;
        quelle.disconnect();
        knoten.disconnect();
      };
    }
    starteMesser(mic, ctx);

    const rate = ctx.sampleRate;
    aufnahme.current = {
      ctx,
      stuecke,
      ersetzeAb: ersetzeBei === null ? null : Math.round(ersetzeBei * rate),
      stopp: () => {
        trennen();
        void ctx.close();
      },
    };
    setLiveSpitzen([]);
    // Ein neues Band beginnt am Abspielkopf (am Ende des Films: vorn); beim
    // Ersetzen läuft der Film ab der Stelle, die dazugehört.
    const filmAb = ersetzeBei === null ? (playhead >= timelineDuration(timeline) ? 0 : playhead) : ab + ersetzeBei;
    if (ersetzeBei === null) setAb(filmAb);
    gestartet.current = performance.now();
    setLaufzeit(0);
    setPlayhead(filmAb);
    setMonitorMuted(true);
    setPlaying(timelineDuration(timeline) > filmAb);
    setPhase('aufnahme');
  };

  // Die Uhr beim Aufnehmen.
  useEffect(() => {
    if (phase !== 'aufnahme') return;
    const timer = setInterval(() => setLaufzeit((performance.now() - gestartet.current) / 1000), 100);
    return () => clearInterval(timer);
  }, [phase]);

  const beendeAufnahme = () => {
    const a = aufnahme.current;
    aufnahme.current = null;
    setPlaying(false);
    setMonitorMuted(false);
    stopMeter();
    releaseMic();
    if (!a) return;
    const rate = a.ctx.sampleRate;
    a.stopp();
    const neu = zusammen(a.stuecke);
    if (neu.length < rate * 0.2) {
      setError('Die Aufnahme war leer -- kam vom Mikrofon nichts an?');
      setPhase(band ? 'band' : 'leer');
      return;
    }
    if (a.ersetzeAb !== null && band) {
      const ersetzt = ersetzeAb(band.werte, neu, a.ersetzeAb);
      setBand({ werte: ersetzt, rate: band.rate });
      setPos(Math.min(ersetzt.length / band.rate, (a.ersetzeAb + neu.length) / band.rate));
    } else {
      setBand({ werte: neu, rate });
      setPos(0);
    }
    setPhase('band');
  };

  // Was gerade gilt, auch in einem Aufruf aus einem älteren Bild heraus.
  const posRef = useRef(pos);
  posRef.current = pos;

  /** Anhören oder anhalten, ab dem Kopf. */
  const spiele = async () => {
    if (!band) return;
    const w = wiedergabe.current;
    if (w?.quelle) {
      setPos(Math.min(dauer, w.ab + (w.ctx.currentTime - w.start)));
      halteWiedergabe();
      return;
    }
    await starteWiedergabe(posRef.current);
  };

  const starteWiedergabe = async (abSekunde: number) => {
    if (!band) return;
    let w = wiedergabe.current;
    if (!w) {
      w = { ctx: new AudioContext(), quelle: null, start: 0, ab: 0, raf: 0 };
      wiedergabe.current = w;
    }
    if (w.ctx.state === 'suspended') await w.ctx.resume();
    const puffer = w.ctx.createBuffer(1, band.werte.length, band.rate);
    puffer.copyToChannel(band.werte as Float32Array<ArrayBuffer>, 0);
    const quelle = w.ctx.createBufferSource();
    quelle.buffer = puffer;
    quelle.connect(w.ctx.destination);
    const von = abSekunde >= dauer - 0.05 ? 0 : Math.max(0, abSekunde);
    quelle.start(0, von);
    w.quelle = quelle;
    w.start = w.ctx.currentTime;
    w.ab = von;
    quelle.onended = () => {
      cancelAnimationFrame(w!.raf);
      w!.quelle = null;
      setSpielt(false);
      setPos(dauer);
    };
    const tick = () => {
      setPos(Math.min(dauer, w!.ab + (w!.ctx.currentTime - w!.start)));
      w!.raf = requestAnimationFrame(tick);
    };
    w.raf = requestAnimationFrame(tick);
    setSpielt(true);
  };

  /** An eine Stelle des Bandes -- beim Spielen spielt es von dort weiter. */
  const springe = (sekunden: number) => {
    const ziel = Math.max(0, Math.min(dauer, sekunden));
    const lief = Boolean(wiedergabe.current?.quelle);
    halteWiedergabe();
    posRef.current = ziel;
    setPos(ziel);
    if (lief) void starteWiedergabe(ziel);
  };

  const behalteAuswahl = (nurDas: boolean) => {
    if (!band || !kuerzen) return;
    const von = Math.round(kuerzen.von * band.rate);
    const bis = Math.round(kuerzen.bis * band.rate);
    const neu = nurDas ? behalte(band.werte, von, bis) : loesche(band.werte, von, bis);
    if (neu.length < band.rate * 0.1) {
      setError('Danach bliebe nichts übrig.');
      return;
    }
    setBand({ werte: neu, rate: band.rate });
    setPos(nurDas ? 0 : Math.min(kuerzen.von, neu.length / band.rate));
    setKuerzen(null);
  };

  /** In den Film: als WAV hochladen, aufbereiten lassen, auf eine Tonspur legen. */
  const einfuegen = async () => {
    if (!project || !band) return;
    halteWiedergabe();
    setError(null);
    const at = ab;
    // Fester Dateiname, nicht das gewählte Zeitformat (S31).
    const name = `Voiceover ${formatTimecode(at, fps).replace(':', '-')}.wav`;
    const file = new File([alsWav(band.werte, band.rate)], name, { type: 'audio/wav' });
    setPhase('hochladen');
    setProgress(0);
    let assetId: string;
    try {
      const { asset } = await api.uploadAsset(project.id, file, setProgress);
      assetId = asset.id;
    } catch (err) {
      setPhase('band');
      setError(err instanceof Error ? err.message : 'Der Upload ist fehlgeschlagen.');
      return;
    }
    setPhase('aufbereiten');
    let laenge = 0;
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 1000));
      const { assets } = await api.assets(project.id);
      const found = assets.find((a) => a.id === assetId);
      if (found?.status === 'ready') {
        laenge = found.durationSec;
        break;
      }
      if (found?.status === 'failed') {
        setPhase('band');
        setError(`Die Aufnahme konnte nicht aufbereitet werden: ${found.error ?? ''}`);
        return;
      }
    }
    await refreshAssets();
    if (laenge <= 0) {
      setPhase('band');
      setError(
        worker.alive === false
          ? 'Der Arbeitsdienst läuft nicht. Die Aufnahme liegt unter „Medien“.'
          : 'Das Aufbereiten dauert zu lange. Die Aufnahme liegt unter „Medien“.',
      );
      return;
    }
    // Auf eine Tonspur, an die Stelle, an der gesprochen wurde. Ist keine
    // dort frei, kommt eine neue dazu -- hinter den letzten Clip geschoben
    // wird eine Aufnahme nie.
    const { timeline: jetzt } = useEditor.getState();
    const audioTracks = jetzt.tracks.filter((t) => t.kind === 'audio');
    let trackId = audioTracks.find((t) => nextFreeStart(jetzt, t.id, at, laenge) === at)?.id;
    const ops: Op[] = [];
    if (!trackId) {
      let n = 1;
      while (jetzt.tracks.some((t) => t.id === `A${n}`)) n++;
      trackId = `A${n}`;
      ops.push({ op: 'addTrack', track: Track.parse({ id: trackId, kind: 'audio' }) } as Op);
    }
    const clipId = newId('c');
    ops.push(
      ausDatei({
        op: 'addClip',
        trackId,
        clip: Clip.parse({ id: clipId, source: { kind: 'asset', assetId }, start: at, duration: laenge, label: 'Voiceover' }),
      } as Op),
    );
    dispatch(ops);
    select([clipId]);
    setPhase('fertig');
    toast('success', `Voiceover eingefügt, ${laenge.toFixed(1)}s ab ${zeit(at)}.`);
  };

  const worker = useWorkerAlive(phase === 'aufbereiten' || phase === 'hochladen');
  const darf = supported && role !== 'viewer';
  const misst = probe || phase === 'aufnahme';
  const stand = aussteuerung(level, hoechste);

  const neuAnfangen = () => {
    halteWiedergabe();
    setBand(null);
    setPos(0);
    setKuerzen(null);
    setLiveSpitzen([]);
    setLevel(0);
    setHoechste(0);
    setError(null);
    setPhase('leer');
  };

  const pegel = (
    <>
      {/* Die Aussteuerung: logarithmisch, mit den Marken, an denen man sich
          ausrichtet. -6 dB ist das Ziel, 0 dB ist zu spät. */}
      <div
        className={`recorder-meter${stand === 'uebersteuert' ? ' heiss' : ''}`}
        data-pegel={misst ? level.toFixed(3) : ''}
        data-spitze={misst ? hoechste.toFixed(3) : ''}
        data-stand={misst ? stand : ''}
        aria-hidden="true"
      >
        <div style={{ width: `${balkenAnteil(level) * 100}%` }} />
        <span className="marke" style={{ left: `${balkenAnteil(0.5) * 100}%` }} />
        <span className="marke voll" style={{ left: `${balkenAnteil(hoechste) * 100}%`, opacity: hoechste > 0 ? 1 : 0 }} />
      </div>
      <p className={`pegel-satz${stand === 'uebersteuert' ? ' heiss' : ''}`} data-pegelsatz>
        {misst ? `${aussteuerungSatz(stand)}${spitzeText(hoechste) ? ` (Spitze ${spitzeText(hoechste)})` : ''}` : 'Noch kein Pegel gemessen.'}
      </p>
    </>
  );

  return (
    <Sheet title="Voiceover" onClose={onClose} voll>
      {!supported ? <p className="hint">Dieser Browser kann nicht aufnehmen. Auf dem iPhone: Safari ab iOS 14.3.</p> : null}

      {phase === 'leer' ? (
        <div className="recorder">
          <div className="recorder-time">ab {zeit(playhead, true)}</div>
          {pegel}
          <button
            className="recorder-button"
            onClick={() => void nimmAuf(null)}
            disabled={!darf}
            aria-label="Aufnahme starten"
            data-band-aufnehmen
          >
            <Icon name="mic" size={30} />
          </button>
          <p className="hint" style={{ textAlign: 'center' }}>
            Nimmt ab dem Abspielkopf auf, der Schnitt läuft stumm mit. Danach anhören, zurückspulen, ersetzen und kürzen.
          </p>
          <button className="chip" data-pegelpruefen onClick={() => void pruefePegel()} disabled={!darf}>
            {probe ? 'Pegel-Prüfung beenden' : 'Erst den Pegel prüfen'}
          </button>
          {/* Aus als Regel: die drei sind für eine Telefonkonferenz gebaut und
              machen eine Stimme dünn und pumpend. An als Schalter, weil ein
              lauter Raum die Ausnahme ist, in der sie helfen. */}
          <label className="spread" style={{ width: '100%', maxWidth: 320 }}>
            <span className="sub">
              Mikrofon aufbereiten
              <span className="hint" style={{ display: 'block' }}>
                Für laute Räume: Rauschfilter und automatische Aussteuerung. Klingt dünner.
              </span>
            </span>
            <input
              type="checkbox"
              data-aufbereiten
              checked={aufbereiten}
              onChange={(e) => {
                setAufbereiten(e.target.checked);
                schreibeSchalter(e.target.checked);
              }}
              style={{ width: 22, height: 22, minHeight: 0 }}
            />
          </label>
        </div>
      ) : null}

      {phase === 'aufnahme' ? (
        <div className="recorder" data-band-nimmt-auf>
          <div className="recorder-time">{bandZeit(laufzeit)}</div>
          <div className="band-welle live" aria-hidden="true">
            {liveSpitzen.map((p, i) => (
              <span key={i} style={{ height: `${Math.max(2, p * 100)}%` }} />
            ))}
          </div>
          {pegel}
          <button className="recorder-button live" onClick={beendeAufnahme} aria-label="Aufnahme beenden" data-band-stopp>
            <span className="stop" />
          </button>
          <p className="hint" style={{ textAlign: 'center' }}>
            {aufnahme.current?.ersetzeAb !== null && band ? 'Ersetzt ab dieser Stelle. Tippen zum Beenden.' : 'Der Schnitt läuft stumm mit. Tippen zum Beenden.'}
          </p>
        </div>
      ) : null}

      {phase === 'band' && band ? (
        <div className="recorder band" data-band data-dauer={dauer.toFixed(2)} data-pos={pos.toFixed(2)}>
          <div className="recorder-time">
            {bandZeit(pos)} <span className="band-gesamt">/ {bandZeit(dauer)}</span>
          </div>
          <Welle
            werte={band.werte}
            dauer={dauer}
            pos={pos}
            kuerzen={kuerzen}
            onPos={(s) => springe(s)}
            onKuerzen={setKuerzen}
          />
          <div className="band-steuerung">
            <button className="ghost" aria-label={`${SPRUNG} Sekunden zurück`} data-band-zurueck onClick={() => springe(pos - SPRUNG)}>
              <span className="band-sprung">↺ {SPRUNG}</span>
            </button>
            <button className="band-spielen" aria-label={spielt ? 'Anhalten' : 'Anhören'} data-band-spielen onClick={() => void spiele()}>
              {spielt ? <Icon name="pause" size={28} /> : <Icon name="play" size={28} />}
            </button>
            <button className="ghost" aria-label={`${SPRUNG} Sekunden vor`} data-band-vor onClick={() => springe(pos + SPRUNG)}>
              <span className="band-sprung">{SPRUNG} ↻</span>
            </button>
          </div>
          {kuerzen ? (
            <>
              <p className="hint" style={{ textAlign: 'center', margin: 0 }}>
                Die gelben Griffe ziehen: {bandZeit(kuerzen.von)} bis {bandZeit(kuerzen.bis)}
              </p>
              <div className="chips" style={{ justifyContent: 'center' }}>
                <button className="chip active" data-band-behalten onClick={() => behalteAuswahl(true)}>
                  Nur das behalten
                </button>
                <button className="chip danger" data-band-loeschen onClick={() => behalteAuswahl(false)}>
                  Das löschen
                </button>
                <button className="chip" onClick={() => setKuerzen(null)}>
                  Abbrechen
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="chips" style={{ justifyContent: 'center' }}>
                <button className="chip" data-band-ersetzen onClick={() => void nimmAuf(pos >= dauer - 0.05 ? dauer : pos)} disabled={!darf}>
                  <Icon name="mic" size={14} /> {pos >= dauer - 0.05 ? 'Weiter aufnehmen' : `Ab ${bandZeit(pos)} ersetzen`}
                </button>
                <button
                  className="chip"
                  data-band-kuerzen
                  onClick={() => {
                    halteWiedergabe();
                    setKuerzen({ von: 0, bis: dauer });
                  }}
                >
                  Kürzen
                </button>
                <button className="chip" data-band-neu onClick={neuAnfangen}>
                  Verwerfen
                </button>
              </div>
              <button className="primary" style={{ width: '100%', maxWidth: 360 }} data-band-einfuegen onClick={() => void einfuegen()} disabled={!darf}>
                In den Film, ab {zeit(ab)}
              </button>
            </>
          )}
        </div>
      ) : null}

      {phase === 'hochladen' ? (
        <div className="recorder">
          <p>Aufnahme wird hochgeladen…</p>
          <div className="progressbar" style={{ width: '100%', maxWidth: 320 }}>
            <div style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        </div>
      ) : null}
      {phase === 'aufbereiten' ? (
        <>
          <WorkerWarning alive={worker.alive} lastError={worker.lastError} />
          <div className="recorder">
            <p>Aufnahme wird aufbereitet…</p>
          </div>
        </>
      ) : null}
      {phase === 'fertig' ? (
        <div className="recorder" data-band-fertig>
          <p>Fertig. Die Aufnahme liegt auf der Tonspur.</p>
          {stand === 'uebersteuert' ? (
            <p className="pegel-satz heiss" data-pegelsatz>
              Diese Aufnahme war übersteuert. Die lauten Stellen knacken; noch einmal mit mehr Abstand ist schneller als jede Rettung.
            </p>
          ) : null}
          <div className="chips" style={{ justifyContent: 'center' }}>
            <button className="chip" onClick={neuAnfangen}>
              Noch eine
            </button>
            <button className="chip active" onClick={onClose}>
              Fertig
            </button>
          </div>
        </div>
      ) : null}
      {error ? (
        <p className="error-text" data-band-fehler>
          {error}
        </p>
      ) : null}
    </Sheet>
  );
}

/**
 * Die Wellenform des Bandes: tippen oder ziehen setzt den Kopf. Beim Kürzen
 * stehen zwei Griffe darauf, und was ausserhalb liegt, ist abgedunkelt.
 */
function Welle({
  werte,
  dauer,
  pos,
  kuerzen,
  onPos,
  onKuerzen,
}: {
  werte: Float32Array;
  dauer: number;
  pos: number;
  kuerzen: { von: number; bis: number } | null;
  onPos: (sekunden: number) => void;
  onKuerzen: (k: { von: number; bis: number }) => void;
}): ReactElement {
  const flaeche = useRef<HTMLDivElement>(null);
  const spitzen = useMemo(() => spitzenVon(werte, 160), [werte]);
  const anteil = (s: number) => (dauer > 0 ? Math.max(0, Math.min(1, s / dauer)) : 0);
  const sekundeBei = (clientX: number): number => {
    const box = flaeche.current?.getBoundingClientRect();
    if (!box || box.width <= 0) return 0;
    return Math.max(0, Math.min(dauer, ((clientX - box.left) / box.width) * dauer));
  };
  const ziehe = (e: React.PointerEvent, was: 'kopf' | 'von' | 'bis') => {
    e.preventDefault();
    e.stopPropagation();
    const ziel = e.currentTarget as HTMLElement;
    ziel.setPointerCapture?.(e.pointerId);
    const setze = (x: number) => {
      const s = sekundeBei(x);
      if (was === 'kopf') onPos(s);
      else if (kuerzen && was === 'von') onKuerzen({ von: Math.min(s, kuerzen.bis - 0.1), bis: kuerzen.bis });
      else if (kuerzen && was === 'bis') onKuerzen({ von: kuerzen.von, bis: Math.max(s, kuerzen.von + 0.1) });
    };
    setze(e.clientX);
    const zug = (ev: PointerEvent) => setze(ev.clientX);
    const los = () => {
      ziel.removeEventListener('pointermove', zug);
      ziel.removeEventListener('pointerup', los);
      ziel.removeEventListener('pointercancel', los);
    };
    ziel.addEventListener('pointermove', zug);
    ziel.addEventListener('pointerup', los);
    ziel.addEventListener('pointercancel', los);
  };
  return (
    <div className="band-welle" ref={flaeche} data-band-welle onPointerDown={(e) => (kuerzen ? undefined : ziehe(e, 'kopf'))}>
      {spitzen.map((p, i) => (
        <span key={i} style={{ height: `${Math.max(2, p * 100)}%` }} />
      ))}
      {kuerzen ? (
        <>
          <div className="band-aus" style={{ left: 0, width: `${anteil(kuerzen.von) * 100}%` }} />
          <div className="band-aus" style={{ left: `${anteil(kuerzen.bis) * 100}%`, right: 0 }} />
          <div className="band-griff" data-band-griff="von" style={{ left: `${anteil(kuerzen.von) * 100}%` }} onPointerDown={(e) => ziehe(e, 'von')} />
          <div className="band-griff" data-band-griff="bis" style={{ left: `${anteil(kuerzen.bis) * 100}%` }} onPointerDown={(e) => ziehe(e, 'bis')} />
        </>
      ) : (
        <div className="band-kopf" style={{ left: `${anteil(pos) * 100}%` }} />
      )}
    </div>
  );
}
